import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "core-js/modules/es.function.name.js";
import "core-js/modules/es.number.constructor.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.regexp.to-string.js";
import React, { useEffect } from 'react';
import { Routes, Route, generatePath } from 'react-router-dom';
import useSearchParameter from 'hooks/useSearchParameter';
import { useFetchUserAnalysisWeeks, useFetchUserHelixAccess } from 'actions/users';
import getUserId from 'utils/session/getUserId';
import createLocation from 'utils/createLocation';
import { toRelativePaths } from 'utils/routes/toRelativePaths';
import { PROFILE_ROUTES } from 'utils/routes';
import config from 'utils/config';
import WeekAnalysisPage from './pages/WeekAnalysisPage';
import MeetingAnalysisPage from './pages/MeetingAnalysisPage';
import SkillAnalysisPage from './pages/SkillAnalysisPage';
import WelcomePage, { HowHelixWorksText, HelixChangeMyMindText } from './pages/WelcomePage';
import HelixNotAvailablePage from './pages/HelixNotAvailablePage';
import SummaryFlagCard from './components/SummaryFlagCard';
import NoTrackedMeetingsEmptyContent from './components/NoTrackedMeetingsEmptyContent';
import RelatedSkillContentButton from './components/RelatedSkillContentButton';
import SkillContentList from './components/SkillContentList';
import HelixEmailLink from './components/HelixEmailLink';
import SettingsLink from './components/SettingsLink';
import { openMindsetAgentWithMessage, useCheckIfWeShowAgentInPage, useHideMindsetAgentDock } from 'utils/mindset';
import { defaultInitialAgentMsgForProfileUserSkills } from 'utils/mindset/mindsetPrompts';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function UserSkillsWidget(_ref) {
  var userId = _ref.userId,
    user = _ref.user,
    userAnalysisRoutes = _ref.userAnalysisRoutes;
  var _useSearchParameter = useSearchParameter('week-commencing'),
    _useSearchParameter2 = _slicedToArray(_useSearchParameter, 2),
    weekCommencing = _useSearchParameter2[0],
    setWeekCommencing = _useSearchParameter2[1];
  var relativeAnalysisRoutes = toRelativePaths(userAnalysisRoutes, userAnalysisRoutes.root);
  var isCurrentUser = Number(userId) === Number(getUserId()) || !userId;
  var _useFetchUserAnalysis = useFetchUserAnalysisWeeks({
      userId: userId
    }),
    weeks = _useFetchUserAnalysis.items,
    weeksLoading = _useFetchUserAnalysis.isLoading,
    weeksError = _useFetchUserAnalysis.isError;
  var _useFetchUserHelixAcc = useFetchUserHelixAccess({
      userId: userId
    }),
    isHelixAccessLoading = _useFetchUserHelixAcc.isLoading,
    helixAccessData = _useFetchUserHelixAcc.data;
  var _ref2 = helixAccessData || {},
    isUserHelixUserInThisHub = _ref2.hasAccess,
    otherHelixDomain = _ref2.otherHelixDomain;
  var isLoading = weeksLoading || isHelixAccessLoading;
  var showWelcomePage = (!weeks || weeks.length === 0) && !isLoading && isCurrentUser;
  var isProfile = userAnalysisRoutes.root === PROFILE_ROUTES.skills;

  // Held for the whole ambiguous-outcome window, not just once WelcomePage's
  // card mounts - otherwise the dock can auto-init before we know this
  // route owns its own agent.
  useHideMindsetAgentDock(isProfile && (isLoading || showWelcomePage));
  var _useCheckIfWeShowAgen = useCheckIfWeShowAgentInPage(),
    hasAgentOnPage = _useCheckIfWeShowAgen.hasAgentOnPage;
  useEffect(function () {
    // hasAgentOnPage guards against leaving the message stuck in the
    // buffer for an unrelated page to pick up. Also wait for
    // isLoading/showWelcomePage: dispatching earlier hits the dock while
    // it's still hidden, and isn't meant for OnboardingMindsetCard anyway.
    if (!isProfile || !hasAgentOnPage || isLoading || showWelcomePage) return;
    openMindsetAgentWithMessage(config.helixMindsetProfileInitialSkillsAnalysisPrompt || defaultInitialAgentMsgForProfileUserSkills, {
      silent: true
    });
  }, [isProfile, hasAgentOnPage, isLoading, showWelcomePage]);
  function getUserSkillLocation(_ref3) {
    var meetingId = _ref3.meetingId,
      skillId = _ref3.skillId,
      searchSkillId = _ref3.searchSkillId;
    if (meetingId) {
      var search = {
        'week-commencing': weekCommencing
      };
      if (searchSkillId) {
        search.skillId = searchSkillId;
      }
      return createLocation(generatePath(userAnalysisRoutes.meeting, {
        meetingId: meetingId,
        userId: userId
      }), search);
    }
    if (skillId) {
      return createLocation(generatePath(userAnalysisRoutes.skill, {
        skillId: skillId,
        userId: userId
      }), {
        'week-commencing': weekCommencing
      });
    }
    return createLocation(generatePath(userAnalysisRoutes.root, {
      userId: userId
    }), {
      'week-commencing': weekCommencing
    });
  }
  if (isLoading) {
    return null;
  }
  return /*#__PURE__*/_jsxs(Routes, {
    children: [/*#__PURE__*/_jsx(Route, {
      path: relativeAnalysisRoutes.skill,
      element: isUserHelixUserInThisHub ? /*#__PURE__*/_jsx(SkillAnalysisPage, {
        isCurrentUser: isCurrentUser,
        weeksLoading: weeksLoading,
        weeksError: weeksError,
        userId: userId,
        user: user,
        weekCommencing: weekCommencing === null || weekCommencing === void 0 ? void 0 : weekCommencing.toString(),
        weeks: weeks,
        setWeekCommencing: setWeekCommencing,
        getUserSkillLocation: getUserSkillLocation
      }) : /*#__PURE__*/_jsx(HelixNotAvailablePage, {
        userId: userId,
        user: user,
        isCurrentUser: isCurrentUser,
        helixHubName: otherHelixDomain === null || otherHelixDomain === void 0 ? void 0 : otherHelixDomain.name,
        helixHubDomain: otherHelixDomain === null || otherHelixDomain === void 0 ? void 0 : otherHelixDomain.url
      })
    }), /*#__PURE__*/_jsx(Route, {
      path: relativeAnalysisRoutes.meeting,
      element: isUserHelixUserInThisHub ? /*#__PURE__*/_jsx(MeetingAnalysisPage, {
        isCurrentUser: isCurrentUser,
        user: user,
        userId: userId,
        getUserSkillLocation: getUserSkillLocation
      }) : /*#__PURE__*/_jsx(HelixNotAvailablePage, {
        userId: userId,
        user: user,
        isCurrentUser: isCurrentUser,
        helixHubName: otherHelixDomain === null || otherHelixDomain === void 0 ? void 0 : otherHelixDomain.name,
        helixHubDomain: otherHelixDomain === null || otherHelixDomain === void 0 ? void 0 : otherHelixDomain.url
      })
    }), /*#__PURE__*/_jsx(Route, {
      path: relativeAnalysisRoutes.root,
      element: isUserHelixUserInThisHub ? showWelcomePage ? /*#__PURE__*/_jsx(WelcomePage, {
        user: user
      }) : /*#__PURE__*/_jsx(WeekAnalysisPage, {
        isCurrentUser: isCurrentUser,
        weeksLoading: weeksLoading,
        weeksError: weeksError,
        userId: userId,
        user: user,
        weekCommencing: weekCommencing === null || weekCommencing === void 0 ? void 0 : weekCommencing.toString(),
        weeks: weeks,
        getUserSkillLocation: getUserSkillLocation,
        setWeekCommencing: setWeekCommencing
      }) : /*#__PURE__*/_jsx(HelixNotAvailablePage, {
        userId: userId,
        user: user,
        isCurrentUser: isCurrentUser,
        helixHubName: otherHelixDomain === null || otherHelixDomain === void 0 ? void 0 : otherHelixDomain.name,
        helixHubDomain: otherHelixDomain === null || otherHelixDomain === void 0 ? void 0 : otherHelixDomain.url
      })
    })]
  });
}
export default UserSkillsWidget;
export { SummaryFlagCard };
export { NoTrackedMeetingsEmptyContent };
export { HelixEmailLink };
export { SettingsLink };
export { HowHelixWorksText };
export { HelixChangeMyMindText };
export { RelatedSkillContentButton };
export { SkillContentList };